import { useState, useEffect } from 'react';
import axios from 'axios';

export interface Timeslot {
  uuid: string;
  startTime: string;
  maxCapacity: number | null;
  activated: boolean;
}

export interface DayAvailability {
  scheduleUuid: string;
  scheduleName: string;
  priority: number;
  timeslots: Timeslot[];
}

export interface ScheduleAvailability {
  startDate: string;
  endDate: string;
  hasYearRoundSchedule: boolean;
  /**
   * Weekdays (0 = Sunday) the year-round schedules run on, or null when they run
   * every day. `availableDates` only covers [startDate, endDate]; beyond that
   * window this is the only thing left to filter on, and treating "year-round"
   * as "every day" is what let a Saturday be picked on a Mon-Fri tour.
   * Optional: an older backend response simply omits it.
   */
  yearRoundDaysOfWeek?: number[] | null;
  availableDates: string[];
  availability: Record<string, DayAvailability>;
}

// Module-level cache + in-flight de-duplication, keyed by
// durationUuid|startDate|endDate. Availability for a given duration + date
// range is effectively static for the session, so once fetched we never hit
// the network again for the same key. This is also what keeps a re-render /
// re-mount churn (e.g. while the user switches currency) from firing the same
// `/availability` request dozens of times — every repeat is a cache hit.
const availabilityCache = new Map<string, ScheduleAvailability>();
const inflightRequests = new Map<string, Promise<ScheduleAvailability>>();

/**
 * Custom hook to fetch schedule availability for a tour duration.
 */
export function useScheduleAvailability(durationUuid: string | null, startDate: string, endDate: string) {
  const key =
    durationUuid && startDate && endDate
      ? `${durationUuid}|${startDate}|${endDate}`
      : null;

  const [availability, setAvailability] = useState<ScheduleAvailability | null>(
    () => (key ? availabilityCache.get(key) ?? null : null)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!key || !durationUuid) {
      setAvailability(null);
      return;
    }

    // Cache hit — serve synchronously, no request.
    const cached = availabilityCache.get(key);
    if (cached) {
      setAvailability(cached);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    // Reuse an in-flight request for the same key so concurrent callers
    // (ChooseDate + ChooseTime, or a re-render burst) share one round-trip.
    let request = inflightRequests.get(key);
    if (!request) {
      request = axios
        .get(
          `${process.env.NEXT_PUBLIC_SOCKET}/api/tour/duration/${durationUuid}/availability`,
          { params: { startDate, endDate } }
        )
        .then((response) => {
          const data = response.data as ScheduleAvailability;
          availabilityCache.set(key, data);
          return data;
        })
        .finally(() => {
          inflightRequests.delete(key);
        });
      inflightRequests.set(key, request);
    }

    request
      .then((data) => {
        if (cancelled) return;
        setAvailability(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching schedule availability:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch availability');
        setAvailability(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, durationUuid, startDate, endDate]);

  return { availability, loading, error };
}
